import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Bell, CalendarDays, Clock, Settings, MessageCircle, Star, ExternalLink, HelpCircle, Film, Images, Sparkles, Code } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useNoIndex } from "@/hooks/useSeo";
import { Logo } from "@/components/Logo";
import { AppointmentsTab } from "@/components/admin/AppointmentsTab";
import { HoursTab } from "@/components/admin/HoursTab";
import { SettingsTab } from "@/components/admin/SettingsTab";
import { WhatsAppTab } from "@/components/admin/WhatsAppTab";
import { NotificationsTab, ReviewsTab } from "@/components/admin/InboxTabs";
import { FaqTab } from "@/components/admin/FaqTab";
import { VideosTab } from "@/components/admin/VideosTab";
import { GalleryTab } from "@/components/admin/gallery/GalleryTab";
import { DecorationsTab } from "@/components/admin/decor/DecorationsTab";
import { CodesTab } from "@/components/admin/codes/CodesTab";

const TABS = [
  { id: "citas", label: "Citas", icon: CalendarDays, C: AppointmentsTab },
  { id: "horarios", label: "Horarios", icon: Clock, C: HoursTab },
  { id: "sitio", label: "Configuración", icon: Settings, C: SettingsTab },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, C: WhatsAppTab },
  { id: "avisos", label: "Avisos", icon: Bell, C: NotificationsTab },
  { id: "opiniones", label: "Opiniones", icon: Star, C: ReviewsTab },
  { id: "preguntas", label: "Preguntas", icon: HelpCircle, C: FaqTab },
  { id: "videos", label: "Videos", icon: Film, C: VideosTab },
  { id: "fotos", label: "Galería fotográfica", icon: Images, C: GalleryTab },
  { id: "decoraciones", label: "Decoraciones", icon: Sparkles, C: DecorationsTab },
  { id: "codigos", label: "Códigos web", icon: Code, C: CodesTab },
];

const useStats = () => {
  const [stats, setStats] = useState(null);
  const last = useRef(null);
  useEffect(() => {
    const load = () => api.get("/admin/stats").then((r) => {
      if (last.current !== null && r.data.unread > last.current) toast.info("Tienes un nuevo aviso en el panel", { duration: 6000 });
      last.current = r.data.unread;
      setStats(r.data);
    }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    window.addEventListener("lg-refresh-stats", load);
    return () => { clearInterval(t); window.removeEventListener("lg-refresh-stats", load); };
  }, []);
  return stats;
};

const Stat = ({ label, value, id }) => (
  <div className="card-dark p-5" data-testid={`stat-${id}`}>
    <p className="eyebrow">{label}</p>
    <p className="mt-2 font-display text-4xl font-black">{value ?? "–"}</p>
  </div>
);

export default function Admin() {
  const { user, logout } = useAuth();
  useNoIndex();
  const stats = useStats();
  const [tab, setTab] = useState("citas");
  useEffect(() => { document.title = "Panel | La Gringa"; }, []);
  return (
    <div className="min-h-screen bg-paper">
      <header className="sticky top-0 z-30 border-b border-line bg-paper/90 backdrop-blur-xl">
        <div className="container-x flex h-16 items-center justify-between gap-3">
          <Link to="/"><Logo size={34} /></Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/" className="hidden sm:flex items-center gap-1 text-mute hover:text-ink"><ExternalLink size={14} /> Ver sitio</Link>
            <span className="hidden md:inline text-mute2">{user?.email}</span>
            <button onClick={logout} className="btn-ghost !px-3 !py-2" data-testid="admin-logout-btn"><LogOut size={16} /> Salir</button>
          </div>
        </div>
      </header>
      <main className="container-x py-8">
        <h1 className="text-3xl sm:text-4xl font-black uppercase">Panel de administración</h1>
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
          <Stat id="today" label="Citas hoy" value={stats?.today} />
          <Stat id="upcoming" label="Próximas" value={stats?.upcoming} />
          <Stat id="pending" label="Pendientes" value={stats?.pending} />
          <Stat id="unread" label="Avisos sin leer" value={stats?.unread} />
        </div>
        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-surface p-1">
            {TABS.map((t) => (
              <TabsTrigger key={t.id} value={t.id} data-testid={`admin-tab-${t.id}`}
                className="gap-2 data-[state=active]:bg-green data-[state=active]:text-white uppercase tracking-wider text-xs font-semibold">
                <t.icon size={15} />{t.label}{t.id === "avisos" && stats?.unread > 0 && <span className="rounded-full bg-red-500 px-1.5 text-[10px] text-white">{stats.unread}</span>}
              </TabsTrigger>
            ))}
          </TabsList>
          {TABS.map((t) => <TabsContent key={t.id} value={t.id} className="mt-6"><t.C /></TabsContent>)}
        </Tabs>
      </main>
    </div>
  );
}
